/**
 * Labstep
 *
 * @module grid/BatchCreateResourceItems/coldefs/amount
 * @desc Column Definition for resource item amount
 */

import type {
  ValueGetterParams,
  ValueSetterParams,
} from 'ag-grid-community';
import type { ResourceItemData } from 'labstep-web/components/ResourceItem/Action/BulkCreate/Modal/type';
import { ICONS } from 'labstep-web/constants/icons';
import { suppressKeyboardEventClearCells } from 'labstep-web/core/DataGrid/CellEditor/utils';
import { DataGridPlaceholder } from 'labstep-web/core/DataGrid/Placeholder';
import type { CellRendererParams } from 'labstep-web/core/DataGrid/types';
import { Icon } from 'labstep-web/core/Icon';
import { GridBatchCreateResourceItemsService } from 'labstep-web/grid/BatchCreateResourceItems/services/grid-batch-create-resource-items-service';
import type { BatchCreateResourceItemsColDef } from 'labstep-web/grid/BatchCreateResourceItems/types';

export const getResourceItemAmountColDef = (
  data: ResourceItemData[],
  setData: React.Dispatch<React.SetStateAction<ResourceItemData[]>>,
): BatchCreateResourceItemsColDef => {
  return {
    colId: 'amount',
    headerName: 'Amount',
    headerComponentParams: {
      icon: ICONS.resource_item.info.quantity,
    },
    editable: true,
    valueGetter: (params: ValueGetterParams) => {
      if (!params.node) {
        return '';
      }
      if (params.node?.data?.amount) {
        return `${params.node?.data?.amount || ''} ${params.node?.data?.unit || ''}`;
      }
      return '';
    },
    valueSetter: (params: ValueSetterParams) => {
      if (!params?.node) {
        return false;
      }
      const newValue = params.newValue || '';

      const regex = /(\d+(?:[.,]\d+)?)(\s*[a-zA-Zµ°±²³´¹ΩΔ\/]*)/;
      const match = newValue.replace(',', '.').match(regex);
      if (!match) {
        GridBatchCreateResourceItemsService.insertErrorInData(
          data,
          setData,
          params,
          `Invalid amount format. Please use the format "number unit" or "number" (e.g. "10 kg" or "10").`,
        );
        return false;
      }
      const amount = match[1];
      const unit = match[2].trim();

      const row = params.node.data;
      row.amount = amount;

      row.unit = unit;

      data[params.node.rowIndex!] = row;
      setData(data);
      GridBatchCreateResourceItemsService.removeErrorInData(
        data,
        setData,
        params,
      );
      return true;
    },
    suppressKeyboardEvent: suppressKeyboardEventClearCells,
    valueClearer: () => null,
    cellRenderer: (params: CellRendererParams<ResourceItemData>) => {
      if (!params.node) {
        return null;
      }

      const err = GridBatchCreateResourceItemsService.findErrorInData(
        data,
        params,
      );

      const rowData = params.node.data;

      if (rowData.amount === undefined || rowData.amount === null) {
        return (
          <span
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            <DataGridPlaceholder
              params={params}
              editable={true}
              placeholder={'Enter'}
            />
            {err && (
              <Icon
                color={'red'}
                name="warning sign"
                popup={{
                  content: err.message,
                }}
              />
            )}
          </span>
        );
      }

      return (
        <span
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          <span>{`${rowData.amount} ${rowData.unit || ''}`}</span>
          {err && (
            <Icon
              color={'red'}
              name="warning sign"
              popup={{
                content: err.message,
              }}
            />
          )}
        </span>
      );
    },
    cellStyle: (params) => {
      if (!params.node) {
        return null;
      }

      const err = GridBatchCreateResourceItemsService.findErrorInData(
        data,
        params,
      );
      if (err) {
        return { backgroundColor: '#F5B1B1' };
      }

      return null;
    },
  };
};
